import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { formatDays, SUBRENTALS_KEY } from './types';

/**
 * „zmień dostępność” next to „max N” (owner 2026-10-24 16:15: „dodatkowa opcja ilość dostępna w dniu dla danego
 * dostawcy”): the quantity the supplier's position has available on the rental's days, typed by hand — it replaces
 * „Ilość posiadanych” on those days in the limit (all events). „Wyczyść” brings the catalog's number back. The same in
 * every rental window (packlist, quote, Dopożyczenia). Shown only to whoever may add rentals and sees the quantities.
 */
export function AvailabilityEditor({ positionId, firstDay, lastDay, manual, label }: { positionId: string; firstDay: string; lastDay: string; manual: ReadonlyArray<{ day: string; quantity: number }>; label?: string }) {
  // the same number typed for every day: shown to change it
  const typed = manual.length > 0 && manual.every((m) => m.quantity === manual[0]!.quantity) ? manual[0]!.quantity : null;
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(typed === null ? '' : String(typed));
  const save = useMutation({
    mutationFn: (quantity: number | null) => api(`/api/subrentals/prices/${positionId}/days`, { method: 'PUT', body: { firstDay, lastDay, quantity } }),
    onSuccess: async () => {
      setOpen(false);
      // „max N” here and in the split's list are read again (the limit changed for these days)
      await queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY });
    },
  });
  const days = formatDays(firstDay, lastDay);
  if (!open) {
    return (
      <button type="button" className="link-button small rental-availability-open" onClick={() => setOpen(true)} title={`Ilość dostępna u dostawcy w dniach ${days}, wpisana ręcznie — zastępuje „Ilość posiadanych” w tych dniach`}>
        zmień dostępność
      </button>
    );
  }
  const n = value.trim() === '' ? null : Number(value);
  const ok = n === null || (Number.isInteger(n) && n >= 0);
  return (
    <span className="rental-availability" role="group" aria-label={label ?? 'Dostępność u dostawcy w tych dniach'}>
      <span className="muted small">dostępne u dostawcy {days}:</span>
      <input
        inputMode="numeric"
        aria-label={`Dostępne u dostawcy (${days})`}
        value={value}
        onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => {
          // inside the rental form: Enter saves this, not the rental
          if (e.key === 'Enter') {
            e.preventDefault();
            if (ok && n !== null) save.mutate(n);
          }
        }}
      />
      <button type="button" className="btn small" disabled={!ok || n === null || save.isPending} onClick={() => save.mutate(n)}>
        Zapisz
      </button>
      {manual.length > 0 && (
        <button type="button" className="btn small ghost" disabled={save.isPending} onClick={() => save.mutate(null)} title="Z powrotem „Ilość posiadanych” z katalogu dostawcy">
          Wyczyść
        </button>
      )}
      <button type="button" className="btn small ghost" onClick={() => setOpen(false)}>
        Anuluj
      </button>
      {save.error && (
        <span className="alert small" role="alert">
          {save.error instanceof ApiError ? save.error.message : 'Nie udało się zapisać'}
        </span>
      )}
    </span>
  );
}
